'use client';
/** @jsxImportSource @emotion/react */

import { type PropsWithChildren, useId, forwardRef } from 'react';

import { boxClasses, generateUtilityClasses } from '@mui/material';

import { Box } from '../box/Box';
import { Card } from '../card/Card';
import { Radio, type RadioProps } from '../radio/Radio';
import { Stack } from '../stack/Stack';

export type RadioCardProps = Pick<
  RadioProps,
  | 'checked'
  | 'disabled'
  | 'inputRef'
  | 'name'
  | 'onChange'
  | 'required'
  | 'sx'
  | 'value'
>;

const radioCardClasses = generateUtilityClasses('RadioCard', [
  'root',
  'radio',
  'card',
  'content',
]);

export { radioCardClasses };

export const RadioCard = forwardRef<
  HTMLDivElement,
  PropsWithChildren<RadioCardProps>
>(
  (
    {
      name,
      checked,
      children,
      disabled,
      inputRef,
      onChange,
      required,
      sx,
      value,
    },
    ref
  ) => {
    const id = useId();
    return (
      <Stack
        ref={ref}
        className={radioCardClasses.root}
        direction="column"
        height="100%"
        sx={[...(Array.isArray(sx) ? sx : [sx]), { position: 'relative' }]}
        width="100%"
      >
        <Radio
          id={id}
          name={name}
          classes={{
            root: radioCardClasses.radio,
          }}
          sx={[
            {
              pointerEvents: 'none',
              position: 'absolute',
              right: (theme) => theme.spacing(1),
              top: (theme) => theme.spacing(1),
            },
            {
              '&.Mui-focusVisible::after': {
                content: 'none',
              },
              [`&.Mui-checked + .${radioCardClasses.card}`]: {
                [`& .${boxClasses.root}`]: {
                  padding: (theme) => theme.spacing(2, 5, 2, 2),
                },
                backgroundColor: (theme) => theme.palette.common.white,
                borderColor: (theme) => theme.palette.primary.main,
                borderWidth: '2px',
              },
              [`&.Mui-disabled + .${radioCardClasses.card}`]: {
                [`& .${boxClasses.root}`]: {
                  '@media (hover: hover)': {
                    '&:hover': {
                      backgroundColor: 'initial',
                    },
                  },
                  borderColor: (theme) => theme.palette.common.white,
                  opacity: (theme) => theme.palette.action.disabledOpacity,
                },
                backgroundColor: 'initial',
                borderColor: (theme) => theme.palette.divider,
              },
              [`&.Mui-focusVisible + .${radioCardClasses.card}::after`]: {
                borderRadius: (theme) => `${theme.ui.focus.borderRadius}`,
                content: '""',
                inset: (theme) => `-${theme.ui.focus.outlineOffset}`,
                outline: (theme) =>
                  `${theme.ui.focus.outlineWidth} solid ${theme.palette.action.focusOutline}`,
                position: 'absolute',
              },
            },
          ]}
          checked={checked}
          disabled={disabled}
          inputRef={inputRef}
          required={required}
          value={value}
          onChange={onChange}
        />
        <Card
          component="label"
          classes={{ root: radioCardClasses.card }}
          htmlFor={id}
          sx={{ height: '100%' }}
          variant="outlined"
        >
          <Box
            sx={{
              '@media (hover: hover)': {
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
              },
              height: '100%',
              // Add 1px to padding to compensate for border changes between states:
              // Default: 1px border + (16px + 1px) padding = 18px total
              // Hover/Checked: 2px border + 16px padding = 18px total
              // This keeps the content area consistent across all states
              padding: (theme) =>
                `calc(${theme.spacing(2)} + 1px) calc(${theme.spacing(5)} + 1px) calc(${theme.spacing(2)} + 1px) calc(${theme.spacing(2)} + 1px)`,
              transition: (theme) =>
                theme.transitions.create(['background-color'], {
                  duration: theme.transitions.duration.short,
                }),
            }}
            className={radioCardClasses.content}
            tabIndex={-1}
          >
            {children}
          </Box>
        </Card>
      </Stack>
    );
  }
);

RadioCard.displayName = 'RadioCard';
